/* eslint-disable import/no-unresolved,react/forbid-prop-types,import/prefer-default-export,react-hooks/exhaustive-deps  */
import React, { useMemo } from 'react';
import {
  FrozenCard,
  VirtualFrozenCard,
  DisabledCard,
  VirtualDisabledCard,
  VirtualCard,
  FallbackCard,
  ActiveCard,
} from 'assets/components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { CardState, resolveCardNetwork } from '../../../../screens/Card/constants';

export const CardDetails = ({
  amount = 0,
  currency = '',
  cardStatus = null,
  cardProvider = null,
  handleFlipAction,
  openCardSettingModal,
  openCardCurrencyNote,
}) => {
  const { t: translate } = useTranslation();
  const displayedCardCurrency =
    cardStatus?.cardCurrency?.toUpperCase() || cardStatus?.currency?.toUpperCase();

  const renderCardState = useMemo(() => {
    if (!cardStatus?.isVirtualCard && cardStatus?.cardState?.toUpperCase() === CardState.BLOCKED) {
      return (
        <FrozenCard
          openCardSettingModal={openCardSettingModal}
          cardProvider={cardProvider}
          cardStatus={cardStatus}
          label={translate('card_frozen')}
        />
      );
    }

    if (
      !cardStatus?.isVirtualCard &&
      [CardState.LOST, CardState.STOLEN, CardState.DISABLED].includes(
        cardStatus?.cardState?.toUpperCase()
      )
    ) {
      return (
        <DisabledCard
          openCardSettingModal={openCardSettingModal}
          label={translate('card_disabled')}
          cardProvider={cardProvider}
          cardStatus={cardStatus}
        />
      );
    }

    if (
      !cardStatus?.isVirtualCard &&
      [CardState.ACTIVE, CardState.DAMAGED, CardState.ACTIVATION].includes(
        cardStatus?.cardState?.toUpperCase()
      )
    ) {
      return (
        <ActiveCard
          openCardSettingModal={openCardSettingModal}
          openCardCurrencyNote={openCardCurrencyNote}
          label={translate('loans_loan_active_label')}
          cardProvider={cardProvider}
          cardStatus={cardStatus}
          handleFlip={handleFlipAction}
          amount={amount}
          currency={currency}
        />
      );
    }

    if (
      cardStatus?.isVirtualCard &&
      [CardState.ACTIVE, CardState.DAMAGED].includes(cardStatus?.cardState?.toUpperCase())
    ) {
      return (
        <VirtualCard
          openCardSettingModal={openCardSettingModal}
          openCardCurrencyNote={openCardCurrencyNote}
          label={translate('loans_loan_active_label')}
          cardProvider={cardProvider}
          cardStatus={cardStatus}
          handleFlip={handleFlipAction}
          amount={amount}
          currency={currency}
        />
      );
    }

    if (
      cardStatus?.isVirtualCard &&
      [CardState.LOST, CardState.STOLEN, CardState.DISABLED].includes(
        cardStatus?.cardState?.toUpperCase()
      )
    ) {
      return (
        <VirtualDisabledCard
          openCardSettingModal={openCardSettingModal}
          label={translate('card_disabled')}
          cardProvider={cardProvider}
          cardStatus={cardStatus}
        />
      );
    }

    if (cardStatus?.isVirtualCard && cardStatus?.cardState?.toUpperCase() === CardState.BLOCKED) {
      return (
        <VirtualFrozenCard
          label={translate('card_frozen')}
          openCardSettingModal={openCardSettingModal}
          cardProvider={cardProvider}
          cardStatus={cardStatus}
        />
      );
    }

    return (
      <FallbackCard
        cardProvider={cardProvider}
        cardNetwork={cardStatus?.network?.toUpperCase() || ''}
        amount={amount}
        currency={currency}
        displayedCardCurrency={displayedCardCurrency}
      />
    );
  }, [cardStatus, cardProvider, amount, currency]);

  const cardNetwork = useMemo(
    () => resolveCardNetwork(cardProvider, cardStatus),
    [cardProvider, cardStatus]
  );

  return (
    <div className="bg-light rounded-3">
      {renderCardState}
      <Tooltip
        id="card-currency-tooltip"
        place="right"
        variant="dark"
        opacity={1}
        className="d-none d-md-block p-4 rounded border-1 custom-tooltip">
        <p>
          {translate('card_currency_note', {
            cardCurrency: displayedCardCurrency,
            type: cardNetwork,
          })}
        </p>
      </Tooltip>
    </div>
  );
};

CardDetails.propTypes = {
  amount: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  cardStatus: PropTypes.object.isRequired,
  cardProvider: PropTypes.string.isRequired,
  handleFlipAction: PropTypes.func.isRequired,
  openCardCurrencyNote: PropTypes.func.isRequired,
  openCardSettingModal: PropTypes.func.isRequired,
};
